import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { API_SCORECARD_FULL_INFO, API_SCORECARD_OWN } from 'constants/api';
import { Scorecard, ScorecardStores } from 'store/interfaces';
import { useScorecardPermissionsFroStatusGetter } from 'hooks';
import {
  ownScorecards,
  directScorecards,
  subordinatesScorecards,
  departmentScorecards,
  selectData,
  updateFullData,
  sharedScorecards,
  teamProfiles,
  userScorecards,
} from 'store/scorecards';
import { generateURL } from 'helpers';
import qs from 'query-string';
import { AxiosError } from 'axios';
import { useAppDispatch } from 'store';

export default function useEditScorecardPageData(
  isPageAccessible: boolean,
  variant: ScorecardStores,
  /**
   * When true (Edit page), block access if `canEdit` is false for the
   * loaded scorecard's current status. When false (View page), only the
   * route-level permission and a 403 from the API gate access — viewing
   * is more permissive than editing (e.g. owners can read their own
   * `completed` scorecard but cannot edit it).
   */
  gateOnEdit = true,
) {
  const dispatch = useAppDispatch();
  const { scorecardId } = useParams();
  const navigate = useNavigate();
  const parsedScorecardId = +scorecardId!;
  const [scorecard, setScorecard] = useState<Scorecard>();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [hasAccessForThisStatus, setHasAccessForThisStatus] =
    useState<boolean>(true);
  const permissionsForStatusGetter = useScorecardPermissionsFroStatusGetter();

  const getFullInfoUrl = (context: string) =>
    `${generateURL(API_SCORECARD_FULL_INFO, {
      id: +scorecardId!,
    })}?${qs.stringify({ context })}`;

  const URLS = {
    [ownScorecards]: `${API_SCORECARD_OWN}/${parsedScorecardId}`,
    [directScorecards]: getFullInfoUrl('direct-subordinates'),
    [subordinatesScorecards]: getFullInfoUrl('all-subordinates'),
    [departmentScorecards]: getFullInfoUrl('department'),
    [sharedScorecards]: getFullInfoUrl('shared'),
    [teamProfiles]: getFullInfoUrl('all-subordinates'),
    [userScorecards]: getFullInfoUrl('user-scorecards'),
  };

  const URL = URLS[variant];

  useEffect(() => {
    setLoading(true);

    dispatch(updateFullData({ id: parsedScorecardId, store: variant }))
      .unwrap()
      .then((data) => {
        if (gateOnEdit) {
          const { canEdit } = permissionsForStatusGetter(data.status);
          if (!canEdit) {
            setHasAccessForThisStatus(false);
          }
        }
        setScorecard(data);
      })
      .catch((e: AxiosError) => {
        if (e.response?.status === 403) {
          setHasAccessForThisStatus(false);
          return;
        }
        setNotFound(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    URL,
    dispatch,
    parsedScorecardId,
    permissionsForStatusGetter,
    setLoading,
    variant,
    gateOnEdit,
  ]);

  // Keep the local snapshot in sync with the slice — saves dispatch
  // `updateFullData` (or `patchScorecard.fulfilled` writes the new payload),
  // so subscribing here lets the page re-render without a full reload.
  const reduxRecord = useSelector(selectData(variant, parsedScorecardId));
  useEffect(() => {
    if (reduxRecord?.data) {
      setScorecard(reduxRecord.data);
    }
  }, [reduxRecord?.data]);

  const onBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return {
    scorecard,
    loading,
    notFound,
    onBackClick,
    hasAccess: isPageAccessible && hasAccessForThisStatus,
  };
}
